import type {
  SanityComplexHero,
  SanityCubeExperienceHero,
  SanityEditorialHero,
  SanityHero,
  SanityPageHero,
  SanityProductHeroMedia,
  SanityProductHeroScrollableVideo,
  SanityRev8Hero,
  SanityScrollableHero,
  SanitySdkDownloadsCard,
  SanitySdkDownloadsHero,
  SanitySecondaryHero,
  SanitySensorConfiguratorHero,
  SanitySimpleHero,
} from "@ouster/shared";

import type { HeroGallerySlideV3 } from "~/features/hero/rev8-hero-v3/gallery";
import {
  toCraftAsset,
  toCraftMedia,
  toCraftMediaArray,
  toCraftThumbnail,
  type CraftMediaBlock,
} from "~/features/sanity/media";
import { buildSanityImageSrc } from "~/features/sanity/media/image-url";
import { toCraftLinkArray } from "~/features/sanity/link";
import { toHtml, toPlainText } from "~/features/sanity/rich-text";

/**
 * Craft hero `__typename`s the `Hero` dispatcher (features/hero) keys on.
 * The Sanity catch-all feeds Craft-shaped hero data into the existing,
 * proven hero components via these typenames (the components call the
 * identity `useFragment`, so the shape is all that matters).
 */
export type CraftHeroTypename =
  | "hero_complexHero_BlockType"
  | "hero_secondaryHero_BlockType"
  | "hero_pageHero_BlockType"
  | "hero_editorialHero_BlockType"
  | "hero_productHeroMedia_BlockType"
  | "hero_productShowcase_BlockType"
  | "hero_productHeroScrollableVideo_BlockType"
  | "hero_scrollableHero_BlockType"
  | "hero_simpleHero_BlockType"
  | "hero_cubeExperience_BlockType"
  | "hero_rev8Hero_BlockType"
  | "hero_sensorConfiguratorHero_BlockType"
  | "hero_sdkDownloadsHero_BlockType";

export type CraftHeroData = Record<string, unknown> & { __typename: CraftHeroTypename };

function compactAssetArray(...assets: ReturnType<typeof toCraftAsset>[]) {
  return assets.filter((asset): asset is NonNullable<typeof asset> => Boolean(asset));
}

function adaptSimpleHero(hero: SanitySimpleHero): CraftHeroData {
  return {
    __typename: "hero_simpleHero_BlockType",
    headline: hero.headline,
    // The view contract uses `text` for the rich-text `subline` field.
    text: toHtml(hero.subline),
    media: toCraftMediaArray(hero.media),
    link: toCraftLinkArray(hero.link),
    layout: hero.layout,
    mediaLayout: hero.mediaLayout,
  };
}

function adaptPageHero(hero: SanityPageHero): CraftHeroData {
  return {
    __typename: "hero_pageHero_BlockType",
    headline: hero.headline,
    // PageHero renders `caption` as plain text (parseTextWithHighlights + <p>), so
    // flatten the (now-rich) subline to a string.
    caption: toPlainText(hero.subline),
    media: toCraftMediaArray(hero.media),
    link: toCraftLinkArray(hero.link),
    layout: hero.layout,
  };
}

function adaptEditorialHero(hero: SanityEditorialHero): CraftHeroData {
  return {
    __typename: "hero_editorialHero_BlockType",
    headline: hero.headline,
    text: toHtml(hero.subline),
    media: toCraftMediaArray(hero.media),
  };
}

function adaptComplexHero(hero: SanityComplexHero): CraftHeroData {
  return {
    __typename: "hero_complexHero_BlockType",
    headline: hero.headline,
    // ComplexHero renders `caption` as plain text (`<p>{caption}</p>`).
    caption: toPlainText(hero.subline),
    media: toCraftMediaArray(hero.media),
    // articles dereference to other documents; content wiring is out of scope.
    articles: [],
    bottom: (hero.bottom || []).map((item) => ({
      __typename: "hero_complexHeroBottom_BlockType" as const,
      title: item.title,
      text: toHtml(item.text),
      link: toCraftLinkArray(item.link),
      media: toCraftMediaArray(item.media),
    })),
  };
}

function adaptSecondaryHero(hero: SanitySecondaryHero): CraftHeroData {
  return {
    __typename: "hero_secondaryHero_BlockType",
    // The view contract exposes the eyebrow as a chip tag.
    tag: hero.eyebrow,
    headline: hero.headline,
    caption: toHtml(hero.subline),
    media: toCraftMediaArray(hero.media),
    link: toCraftLinkArray(hero.link),
    supportLink: toCraftLinkArray(hero.supportLink),
    articles: [],
    widgetImage: compactAssetArray(toCraftAsset(hero.widgetImage)),
    widgetDate: hero.widgetDate,
    widgetTitle: hero.widgetTitle,
    widgetUrl: hero.widgetUrl,
  };
}

function adaptScrollableHero(hero: SanityScrollableHero): CraftHeroData {
  return {
    __typename: "hero_scrollableHero_BlockType",
    // The view contract exposes the eyebrow as a small uppercase tag.
    tag: hero.eyebrow,
    headline: hero.headline,
    text: toHtml(hero.subline),
    link: toCraftLinkArray(hero.link),
    media: toCraftMediaArray(hero.media),
    label: hero.label,
    hasOverlay: hero.hasOverlay,
    headlines: (hero.headlines || []).map((item) => ({
      __typename: "hero_scrollableHeroHeadline_BlockType" as const,
      topDistance: item.topDistance,
      bottomDistance: item.bottomDistance,
      headline: item.headline,
    })),
  };
}

function toThumbnailBlock(...candidates: Array<SanityRev8Hero["media"]>): CraftMediaBlock | null {
  for (const candidate of candidates) {
    const asset = toCraftThumbnail(candidate);
    if (asset) return { __typename: "baseMedia_image_BlockType", imageAsset: [asset] };
  }
  return null;
}

/**
 * 3.0 hero gallery: the hero's own media is slide one, `gallery[]` adds the rest. Empty without
 * gallery slides, so a plain hero keeps rendering its single media.
 */

function adaptRev8HeroGallery(hero: SanityRev8Hero): HeroGallerySlideV3[] {
  if (!hero.gallery?.length) return [];

  const slides = [{ _key: "media", media: hero.media, thumbnail: undefined }, ...hero.gallery].flatMap((slide) => {
    const media = toCraftMedia(slide.media);
    return media ? [{ key: slide._key, media, thumbnail: toThumbnailBlock(slide.thumbnail, slide.media) }] : [];
  });

  return slides.length > 1 ? slides : [];
}

function adaptRev8Hero(hero: SanityRev8Hero): CraftHeroData {
  return {
    __typename: "hero_rev8Hero_BlockType",
    headline: hero.headline,
    caption: toHtml(hero.subline),
    media: toCraftMediaArray(hero.media),
    mediaShade: hero.mediaShade,
    gallery: adaptRev8HeroGallery(hero),
    link: toCraftLinkArray(hero.link),
    supportLink: toCraftLinkArray(hero.supportLink),
    logo: compactAssetArray(toCraftAsset(hero.logo)),
  };
}

function adaptSdkDownloadsCard(card: SanitySdkDownloadsCard) {
  if (card._type === "sdkDownloadsLinkCard") {
    return {
      __typename: "sdkDownloadsCards_linkCard_BlockType" as const,
      title: card.title,
      description: card.description,
      link: toCraftLinkArray(card.link),
    };
  }

  return {
    __typename: "sdkDownloadsCards_copyCard_BlockType" as const,
    title: card.title,
    description: card.description,
    command: card.command,
    buttonLabel: card.buttonLabel,
  };
}

function adaptSdkDownloadsHero(hero: SanitySdkDownloadsHero): CraftHeroData {
  return {
    __typename: "hero_sdkDownloadsHero_BlockType",
    media: toCraftMediaArray(hero.media),
    headline: hero.headline,
    // SdkDownloadsHero renders `subtitle` as plain text (`<p>{subtitle}</p>`).
    subtitle: toPlainText(hero.subline),
    cards: (hero.cards || []).map(adaptSdkDownloadsCard),
  };
}

export function adaptCubeExperienceHero(hero: SanityCubeExperienceHero): CraftHeroData {
  return {
    __typename: "hero_cubeExperience_BlockType",
    eyebrow: hero.eyebrow,
    headline: hero.headline,
    text: toHtml(hero.subline),
    link: toCraftLinkArray(hero.link),
    unlockedEyebrow: hero.unlockedEyebrow,
    unlockedHeadline: hero.unlockedHeadline,
    unlockedText: toHtml(hero.unlockedSubline),
    unlockedLink: toCraftLinkArray(hero.unlockedLink),
    mode: hero.mode,
    users: (hero.users || []).map((row) => ({ label: row.label, value: row.value })),
    features: (hero.features || []).map((feature) => ({
      __typename: "features_feature_BlockType" as const,
      unlockAt: feature.unlockAt,
      title: feature.title,
      text: toHtml(feature.text),
      media: toCraftMediaArray(feature.media),
      link: toCraftLinkArray(feature.link),
    })),
  };
}

function adaptProductHeroMedia(hero: SanityProductHeroMedia): CraftHeroData {
  return {
    __typename: "hero_productHeroMedia_BlockType",
    titleSize: hero.titleSize,
    media: toCraftMediaArray(hero.media),
    links: (hero.links || []).map((item) => ({
      __typename: "productHeroLinks_link_BlockType" as const,
      link: toCraftLinkArray(item.link),
    })),
    // Read only by the 3.0 Sensor Hero (`product-hero-v3`); the 2.x hero ignores them.
    // `heroCaption` overrides the document's `productCaption` there, which also feeds the
    // product cards, so a page can headline differently from its cards.
    heroCaption: hero.heroCaption,
    button: toCraftLinkArray(hero.button),
    stats: (hero.stats || []).map((stat) => ({ chip: stat.chip, label: stat.label, value: stat.value })),
  };
}

/**
 * A `productHeroMedia` is rich (renders via `ProductShowcase`) when it carries
 * the text/spec content the showcase layout needs. A bare one (media + links
 * only, e.g. OSDome) keeps the full-screen `ProductHeroMedia`.
 */
function isRichProductHero(hero: SanityProductHeroMedia): boolean {
  return Boolean(
    hero.headline ||
      (Array.isArray(hero.subline) && hero.subline.length > 0) ||
      (Array.isArray(hero.description) && hero.description.length > 0) ||
      (hero.specs && hero.specs.length > 0)
  );
}

/**
 * Map a content-rich `productHeroMedia` into the shape `ProductShowcase`
 * consumes. The component runs the identity `useFragment` on the Sanity
 * catch-all and destructures the fragment's *aliased* keys, so the keys here
 * must match those aliases (title/subtitle/description/link/specs/button/
 * supportLink/accordions/mediaItems).
 */
function adaptProductShowcase(hero: SanityProductHeroMedia): CraftHeroData {
  const mediaItems =
    hero.mediaItems && hero.mediaItems.length > 0 ? hero.mediaItems : hero.media ? [{ media: hero.media }] : [];

  return {
    __typename: "hero_productShowcase_BlockType",
    title: hero.headline,
    // ProductShowcase renders `subtitle` as plain text inside an <h1> spacer, so we
    // flatten the (now-rich) subline to a string here instead of emitting HTML.
    subtitle: toPlainText(hero.subline),
    description: toHtml(hero.description),
    link: toCraftLinkArray(hero.link),
    specs: (hero.specs || []).map((row) => ({ label: row.label, value: row.value })),
    button: toCraftLinkArray(hero.button),
    supportLink: toCraftLinkArray(hero.supportLink),
    accordions: (hero.accordions || []).map((row) => ({ label: row.label, value: row.value })),
    mediaItems: mediaItems.map((item) => ({ media: toCraftMediaArray(item.media) })),
  };
}

function adaptProductHeroScrollableVideo(hero: SanityProductHeroScrollableVideo): CraftHeroData {
  return {
    __typename: "hero_productHeroScrollableVideo_BlockType",
    // Route-level title is layered in later via `withRouteContext` — keep the
    // hero's own `headline` here so it can take priority when present.
    headline: hero.headline,
    frames: (hero.frames || [])
      .map((frame) => toCraftAsset(frame))
      .filter((asset): asset is NonNullable<typeof asset> => Boolean(asset))
      // `oriWidth`/`oriHeight` describe the cropped box, so the frame url has to carry
      // the crop too — the scroll sequence loads these directly, bypassing `Image`.
      .map((asset) => ({
        src: asset.src ? buildSanityImageSrc(asset.src, { edit: asset.imageEdit, w: asset.oriWidth }) : asset.src,
        width: asset.oriWidth,
        height: asset.oriHeight,
      })),
    bottomLeftText: hero.bottomLeftText,
    bottomRightText: toHtml(hero.bottomRightText),
    bottomRightLink: toCraftLinkArray(hero.bottomRightLink),
    links: (hero.links || []).map((item) => ({
      __typename: "productHeroLinks_link_BlockType" as const,
      link: toCraftLinkArray(item.link),
    })),
  };
}

function adaptSensorConfiguratorHero(hero: SanitySensorConfiguratorHero): CraftHeroData {
  return {
    __typename: "hero_sensorConfiguratorHero_BlockType",
    configuratorSensor: hero.configuratorSensor,
    link: toCraftLinkArray(hero.link),
  };
}

/**
 * Map a Sanity hero (the first entry of a route document's `hero[]`) into the
 * Craft-shaped data the existing `Hero` dispatcher consumes. Returns `null`
 * for unknown / unmapped hero types so the catch-all can skip rendering.
 */
export function adaptSanityHeroToCraft(hero?: SanityHero | null): CraftHeroData | null {
  if (!hero?._type) {
    return null;
  }

  switch (hero._type) {
    case "simpleHero":
      return adaptSimpleHero(hero);
    case "pageHero":
      return adaptPageHero(hero);
    case "editorialHero":
      return adaptEditorialHero(hero);
    case "complexHero":
      return adaptComplexHero(hero);
    case "secondaryHero":
      return adaptSecondaryHero(hero);
    case "scrollableHero":
      return adaptScrollableHero(hero);
    case "rev8Hero":
      return adaptRev8Hero(hero);
    case "sdkDownloadsHero":
      return adaptSdkDownloadsHero(hero);
    case "productHeroMedia":
      return isRichProductHero(hero) ? adaptProductShowcase(hero) : adaptProductHeroMedia(hero);
    case "productHeroScrollableVideo":
      return adaptProductHeroScrollableVideo(hero);
    case "sensorConfiguratorHero":
      return adaptSensorConfiguratorHero(hero);
    default:
      return null;
  }
}
